
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { adminService } from '@/services/admin';
import { useToast } from '@/hooks/use-toast';

const Settings = () => {
  const [settings, setSettings] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await adminService.getSystemSettings();
      const settingsMap = data.reduce((acc: any, setting: any) => {
        acc[setting.setting_key] = setting.setting_value;
        return acc;
      }, {});
      setSettings(settingsMap);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        variant: 'destructive',
        title: '載入失敗',
        description: '無法載入系統設定',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSetting = async (key: string, value: string) => {
    try {
      await adminService.updateSystemSetting(key, value);
      setSettings({ ...settings, [key]: value });
    } catch (error) {
      console.error('Error updating setting:', error);
      throw error;
    }
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    try {
      const updates = Object.entries(settings).map(([key, value]) =>
        adminService.updateSystemSetting(key, value as string)
      );
      await Promise.all(updates);
      toast({
        title: '儲存成功',
        description: '系統設定已更新',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        variant: 'destructive',
        title: '儲存失敗',
        description: '無法更新系統設定',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">系統設定</h1>
          <p className="text-gray-600 dark:text-gray-400">管理前台系統相關設定</p>
        </div>
        <Button onClick={handleSaveAll} disabled={isSaving}>
          {isSaving ? '儲存中...' : '儲存所有設定'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 前台公告 */}
        <Card>
          <CardHeader>
            <CardTitle>前台公告訊息</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="announcement">公告內容</Label>
              <Textarea
                id="announcement"
                placeholder="輸入前台公告訊息"
                value={settings.site_announcement || ''}
                onChange={(e) => setSettings({ ...settings, site_announcement: e.target.value })}
                rows={4}
              />
              <p className="text-sm text-gray-500 mt-2">
                此訊息將顯示在前台首頁頂部
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 功能開關 */}
        <Card>
          <CardHeader>
            <CardTitle>功能開關</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="allow-submissions">開放投稿</Label>
                <p className="text-sm text-gray-500">允許用戶提交新的租屋評論</p>
              </div>
              <Switch
                id="allow-submissions"
                checked={settings.allow_submissions === 'true'}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, allow_submissions: checked ? 'true' : 'false' })
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="allow-comments">開放留言</Label>
                <p className="text-sm text-gray-500">允許用戶在評論下方留言</p>
              </div>
              <Switch
                id="allow-comments"
                checked={settings.allow_comments === 'true'}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, allow_comments: checked ? 'true' : 'false' })
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="require-verification">Email 驗證</Label>
                <p className="text-sm text-gray-500">新用戶註冊需要驗證 Email</p>
              </div>
              <Switch
                id="require-verification"
                checked={settings.require_email_verification === 'true'}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, require_email_verification: checked ? 'true' : 'false' })
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* 會員限制 */}
        <Card>
          <CardHeader>
            <CardTitle>會員限制</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="member-limit">會員上限</Label>
              <Input
                id="member-limit"
                type="number"
                placeholder="輸入會員上限數量"
                value={settings.member_limit || ''}
                onChange={(e) => setSettings({ ...settings, member_limit: e.target.value })}
              />
              <p className="text-sm text-gray-500 mt-2">
                設定為 0 表示無限制
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 其他設定 */}
        <Card>
          <CardHeader>
            <CardTitle>其他設定</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-gray-600">
              <p className="font-medium mb-2">系統資訊：</p>
              <p>• 資料庫連線狀態：正常</p>
              <p>• 快取系統：啟用</p>
              <p>• 檔案上傳：啟用</p>
              <p>• 郵件服務：啟用</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
